import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	CustomIconButton,
	CustomTextField,
	CustomTooltip,
	IconComponent,
	VerticalDivider,
	createNotification,
	icon,
} from "../../../../../../../common/components";
import styles from "./PanelHeader.module.css";
import { SENTENCE_TYPE } from "../../../../../constants/constants";
import { SegFlags, isSegFeatureEnabled, translate } from "../../../../../../../common/providers";
import ReviewRedirectionId from "../ChatRedirection/ReviewRedirectionId";
import { useApi } from "../../../../../../../common/hooks";
import { RequirementParameterService } from "../../../../../../../api";
import { hasProperty } from "../../../../../../../common/utils";
import { updateNode } from "../../../../../slice/pdf/pdf-slice";
import { setFinalParameters } from "../../../../../slice/document/document-slice";
import { hasUpdateRights } from "../../../../../utils/utils";

const PanelHeader = ({
	confidence,
	selectedContent,
	informationId,
	isRequirement,
	onAutoScroll,
	onClose,
	projectName,
	updatedInformationId,
	updatedInformationType,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const finalParameters = useSelector(({ srDocument }) => srDocument.finalParameters);
	const [newClientReqId, setNewClientReqId] = useState(null);
	const mode = useSelector(({ srDocument }) => srDocument.mode);
	const roles = useSelector(({ srDocument }) => srDocument.roles);
	const { call: updateParameters } = useApi(RequirementParameterService.updateParameters);
	const dispatch = useDispatch();
	const inputRef = useRef(null);

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditing]);
	const handleEditReqName = () => {
		setNewClientReqId(null);
		setIsEditing(true);
	};
	const checkId = (origin, destination) => {
		if (hasProperty(origin, "id")) {
			if (!origin.id || origin.id.length === 0) {
				destination.id = null;
			}
		}
	};
	const cleanParameters = (params) => {
		checkId(finalParameters, params);
		return params;
	};
	const handleSubmitParameters = (payload) => {
		let mergedParameters;
		if (newClientReqId !== null) {
			mergedParameters = { ...finalParameters, clientId: newClientReqId || null, reqTypeSource: "CLIENT" };
		} else {
			mergedParameters = payload || finalParameters;
		}
		updateParameters({ reqId: informationId }, cleanParameters(mergedParameters))
			.then((data) => {
				const updates = [
					{ property: "parameters", value: data },
					{ property: "clientRequirementId", value: data?.clientId },
				];
				dispatch(
					updateNode({
						mode,
						informationId: selectedContent?.informationId || informationId,
						pageConditions: { page: selectedContent.pageStart },
						updates,
					})
				);
				dispatch(
					setFinalParameters({
						parameterId: selectedContent?.informationId,
						finalParameters: data,
					})
				);
				createNotification({
					type: "success",
					message: translate("smart-review.additional.notification.update-success", {
						reqId: selectedContent?.informationId,
					}),
				});
				setNewClientReqId(null);
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleSend = () => {
		setIsEditing(false);
		handleSubmitParameters();
	};
	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSend();
		}
	};
	const renderReqHeader = () => {
		if (!isEditing) {
			return (
				<>
					<IconComponent className={styles.infoIcon} color="var(--red-500)" icon={icon.faAsterisk} />
					{(newClientReqId !== null ? newClientReqId : selectedContent?.clientRequirementId?.toUpperCase()) ||
						`REQ_${informationId}`}
					{hasUpdateRights(roles) && (
						<CustomTooltip
							placement="top"
							title={translate("smart-review.right-panel.menu.edit-requirement-id")}
						>
							<CustomIconButton
								icon={icon.faPen}
								iconColor="var(--grey-400)"
								size="sm"
								onClick={handleEditReqName}
							/>
						</CustomTooltip>
					)}
				</>
			);
		}
		return (
			<span className={styles.header__desc}>
				<IconComponent className={styles.infoIcon} color="var(--red-500)" icon={icon.faAsterisk} />
				<CustomTextField
					className={{ root: styles.editFiledContainerContainer }}
					data-testid="quick.edit.textfield"
					inputProps={{
						className: styles.editFiledInput,
					}}
					inputRef={inputRef}
					placeholder={translate("smart-review.additional.not-from-client-placeholder")}
					type="text"
					value={
						newClientReqId !== null
							? newClientReqId
							: selectedContent?.clientRequirementId?.toUpperCase() ?? ""
					}
					variant="outlined"
					onBlur={() => setIsEditing(false)}
					onChange={(e) => setNewClientReqId(e.target.value)}
					onKeyDown={handleKeyDown}
				/>
				<CustomIconButton
					btnClassName={styles.btnClassName}
					icon={icon.faPaperPlane}
					iconClassName={styles.iconClassName}
					size="sm"
					tooltip={translate("common:comment.send-button")}
					variant="outlined"
					onMouseDown={(e) => {
						e.preventDefault();
						handleSend();
					}}
				/>
			</span>
		);
	};

	const headerRender = (isInformationType) => {
		if (isInformationType || !isSegFeatureEnabled(SegFlags.PROJECT_REQUIREMENTS)) {
			return (
				<>
					<IconComponent className={styles.infoIcon} color="var(--grey-500)" icon={icon.faStream} />
					{selectedContent?.clientRequirementId?.toUpperCase() || `INFO_${informationId}`}
				</>
			);
		}
		return renderReqHeader();
	};

	return (
		<div className={styles.panel__header}>
			<div className={styles.mainContainer}>
				<div className={styles.mainContainer__header} data-testid={`header.info.${informationId}`}>
					<CustomIconButton
						className={styles.header__icon}
						data-testid="panel.close.btn"
						icon={icon.faTimes}
						iconClassname={styles["header__icon--large"]}
						onClick={onClose}
					/>
					<span className={styles.header__text} translate="no">
						{(informationId === updatedInformationId &&
							headerRender(updatedInformationType === SENTENCE_TYPE.INFORMATION)) ||
							headerRender(!isRequirement)}
					</span>
					{isSegFeatureEnabled(SegFlags.CHAT_PROJECT) && <ReviewRedirectionId />}
				</div>
				<CustomIconButton
					data-testid="panel.scroll.btn"
					icon={icon.faLocationCrosshairs}
					tooltip={translate("smart-review.right-panel.menu.scroll-button")}
					variant="outlined"
					onClick={onAutoScroll}
				/>
			</div>
			{(projectName || confidence) && (
				<div className={styles.secondaryContainer}>
					{projectName && (
						<CustomTooltip showWhenOverflow title={projectName}>
							<span className={styles.projectName}>{projectName}</span>
						</CustomTooltip>
					)}
					{projectName && confidence && <VerticalDivider className={styles.verticalDivider} />}
					{confidence && <span>{Math.floor(confidence * 100)}%</span>}
				</div>
			)}
		</div>
	);
};

export default PanelHeader;
